// Client currently opened by the advisor.
//
// Every screen of a client file reads this store instead of refetching, so the
// banner, the tabs and the simulator always describe the same person. The store
// exposes the six states of the interface contract: loading, empty (no client
// selected), not found, network incident with a retry, server failure carrying a
// request identifier, and the nominal state.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { ErreurHorizon, obtenirClient, toErreur } from '@horizon/api-client';
import type { Client } from '@horizon/types';

export const useClientCourantStore = defineStore('client-courant', () => {
  const client = ref<Client | null>(null);
  const chargement = ref(false);
  const erreur = ref<ErreurHorizon | null>(null);
  /** True when the identifier resolves to nothing inside the caller perimeter (RLS). */
  const introuvable = ref(false);

  /** Identifier of the in flight request, so a stale answer never overwrites a fresh one. */
  let jeton = 0;
  /** Last identifier asked for, replayed by reessayer(). */
  let dernierId: string | null = null;

  const estVide = computed(
    () => !chargement.value && erreur.value === null && client.value === null,
  );
  const erreurReseau = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && erreur.value.reseau ? erreur.value : null,
  );
  const erreurServeur = computed<ErreurHorizon | null>(() =>
    erreur.value !== null && !erreur.value.reseau ? erreur.value : null,
  );
  const nomComplet = computed(() =>
    client.value === null ? '' : `${client.value.prenom} ${client.value.nom}`,
  );
  const initiales = computed(() => {
    if (client.value === null) {
      return '';
    }
    return `${client.value.prenom.charAt(0)}${client.value.nom.charAt(0)}`.toUpperCase();
  });

  /** Load a client by identifier. Reloading the same identifier is a no-op refresh. */
  async function charger(id: string): Promise<void> {
    dernierId = id;
    jeton += 1;
    const courant = jeton;
    chargement.value = true;
    erreur.value = null;
    introuvable.value = false;
    try {
      const resultat = await obtenirClient(id);
      if (courant !== jeton) {
        return;
      }
      client.value = resultat;
      introuvable.value = resultat === null;
    } catch (cause) {
      if (courant !== jeton) {
        return;
      }
      erreur.value = toErreur(cause);
      client.value = null;
    } finally {
      if (courant === jeton) {
        chargement.value = false;
      }
    }
  }

  /** Replay the last load, wired to the retry action of the network banner. */
  async function reessayer(): Promise<void> {
    if (dernierId === null) {
      return;
    }
    await charger(dernierId);
  }

  /** Clear the selection, on sign out or when leaving the client area. */
  function reinitialiser(): void {
    jeton += 1;
    dernierId = null;
    client.value = null;
    chargement.value = false;
    erreur.value = null;
    introuvable.value = false;
  }

  return {
    client,
    chargement,
    erreur,
    introuvable,
    estVide,
    erreurReseau,
    erreurServeur,
    nomComplet,
    initiales,
    charger,
    reessayer,
    reinitialiser,
  };
});
